import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// Every backend response here goes through the shared success() response
// util (see src/utils/response.js), which wraps the payload as
// { success, data: {...} } — so unwrapping is always response.data.data.*,
// same convention as instagramSlice.js.

// ─── Credentials ────────────────────────────────────────
export const saveMetaCredentials = createAsyncThunk(
  'metaAds/saveCredentials',
  async ({ appId, appSecret, webhookVerifyToken }, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/meta-ads/oauth/credentials', { appId, appSecret, webhookVerifyToken });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to save credentials');
    }
  }
);

export const fetchCredentialsStatus = createAsyncThunk(
  'metaAds/fetchCredentialsStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/oauth/credentials');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch credentials status');
    }
  }
);

// ─── OAuth ───────────────────────────────────────────────
export const fetchOAuthUrls = createAsyncThunk(
  'metaAds/fetchOAuthUrls',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/oauth/urls');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch OAuth URLs');
    }
  }
);

export const fetchMetaOAuthUrl = createAsyncThunk(
  'metaAds/fetchOAuthUrl',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/oauth/url');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to get OAuth URL');
    }
  }
);

export const connectWithAccessToken = createAsyncThunk(
  'metaAds/connectWithAccessToken',
  async (accessToken, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/meta-ads/oauth/connect-token', { accessToken });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to connect with that access token');
    }
  }
);

export const fetchConnectionStatus = createAsyncThunk(
  'metaAds/fetchConnectionStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/oauth/status');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch connection status');
    }
  }
);

export const disconnectMeta = createAsyncThunk(
  'metaAds/disconnectMeta',
  async (_, { rejectWithValue }) => {
    try {
      await api.delete('/marketing/meta-ads/oauth/disconnect');
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to disconnect');
    }
  }
);

// ─── Ad Accounts ─────────────────────────────────────────
export const fetchAdAccounts = createAsyncThunk(
  'metaAds/fetchAdAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/accounts');
      return response.data.data.accounts || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ad accounts');
    }
  }
);

// ─── Campaigns ───────────────────────────────────────────
export const fetchCampaigns = createAsyncThunk(
  'metaAds/fetchCampaigns',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/campaigns', { params });
      return response.data.data.campaigns || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch campaigns');
    }
  }
);

export const createCampaign = createAsyncThunk(
  'metaAds/createCampaign',
  async ({ adAccountId, campaignData }, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/meta-ads/campaigns', {
        adAccountId,
        campaignData
      });
      return response.data.data.campaign;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create campaign');
    }
  }
);

export const updateCampaign = createAsyncThunk(
  'metaAds/updateCampaign',
  async ({ id, campaignData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/marketing/meta-ads/campaigns/${id}`, {
        campaignData
      });
      return response.data.data.campaign;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update campaign');
    }
  }
);

export const deleteCampaign = createAsyncThunk(
  'metaAds/deleteCampaign',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/marketing/meta-ads/campaigns/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete campaign');
    }
  }
);

export const duplicateCampaign = createAsyncThunk(
  'metaAds/duplicateCampaign',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/marketing/meta-ads/campaigns/${id}/duplicate`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to duplicate campaign');
    }
  }
);

export const fetchAnalyticsBreakdown = createAsyncThunk(
  'metaAds/fetchAnalyticsBreakdown',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/analytics/breakdown', { params });
      return response.data.data.rows || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load breakdown');
    }
  }
);

// ─── Ad Sets ─────────────────────────────────────────────
export const createAdSet = createAsyncThunk(
  'metaAds/createAdSet',
  async ({ campaignId, adSetData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/marketing/meta-ads/campaigns/${campaignId}/adsets`, {
        adSetData
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create ad set');
    }
  }
);

export const fetchAdSets = createAsyncThunk(
  'metaAds/fetchAdSets',
  async (campaignId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/marketing/meta-ads/campaigns/${campaignId}/adsets`);
      return { campaignId, adSets: response.data.data.adSets || [] };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ad sets');
    }
  }
);

export const updateAdSet = createAsyncThunk(
  'metaAds/updateAdSet',
  async ({ campaignId, id, updates }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/marketing/meta-ads/campaigns/${campaignId}/adsets/${id}`, updates);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update ad set');
    }
  }
);

// ─── Creative & Ad ──────────────────────────────────────
export const uploadCreativeImage = createAsyncThunk(
  'metaAds/uploadCreativeImage',
  async ({ campaignId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.post(`/marketing/meta-ads/campaigns/${campaignId}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to upload image');
    }
  }
);

export const uploadCreativeVideo = createAsyncThunk(
  'metaAds/uploadCreativeVideo',
  async ({ campaignId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('video', file);
      const response = await api.post(`/marketing/meta-ads/campaigns/${campaignId}/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // a video can take a few minutes to upload
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to upload video');
    }
  }
);

export const createAd = createAsyncThunk(
  'metaAds/createAd',
  async ({ campaignId, adData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/marketing/meta-ads/campaigns/${campaignId}/ads`, adData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create ad');
    }
  }
);

export const fetchAds = createAsyncThunk(
  'metaAds/fetchAds',
  async (campaignId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/marketing/meta-ads/campaigns/${campaignId}/ads`);
      return { campaignId, ads: response.data.data.ads || [] };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ads');
    }
  }
);

export const updateAd = createAsyncThunk(
  'metaAds/updateAd',
  async ({ campaignId, id, updates }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/marketing/meta-ads/campaigns/${campaignId}/ads/${id}`, updates);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update ad');
    }
  }
);

export const publishAd = createAsyncThunk(
  'metaAds/publishAd',
  async ({ campaignId, id }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/marketing/meta-ads/campaigns/${campaignId}/ads/${id}/publish`);
      return { campaignId, ...response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to publish ad');
    }
  }
);

// ─── Pages ───────────────────────────────────────────────
export const fetchPages = createAsyncThunk(
  'metaAds/fetchPages',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/pages');
      return response.data.data.pages || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch pages');
    }
  }
);

// ─── Targeting Search ───────────────────────────────────
export const searchInterests = createAsyncThunk(
  'metaAds/searchInterests',
  async (query, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/targeting/interests', { params: { q: query } });
      return response.data.data.interests || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to search interests');
    }
  }
);

export const searchLocations = createAsyncThunk(
  'metaAds/searchLocations',
  async (query, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/targeting/locations', { params: { q: query } });
      return response.data.data.locations || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to search locations');
    }
  }
);

export const searchLanguages = createAsyncThunk(
  'metaAds/searchLanguages',
  async (query, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/targeting/languages', { params: { q: query } });
      return response.data.data.languages || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to search languages');
    }
  }
);

export const fetchCustomAudiences = createAsyncThunk(
  'metaAds/fetchCustomAudiences',
  async (adAccountId, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/targeting/custom-audiences', { params: { adAccountId } });
      return response.data.data.audiences || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load custom audiences');
    }
  }
);

// ─── Custom/Lookalike Audiences (built from this CRM's own data) ─────────
export const fetchManagedAudiences = createAsyncThunk(
  'metaAds/fetchManagedAudiences',
  async (adAccountId, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/audiences', { params: { adAccountId } });
      return response.data.data.audiences || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load audiences');
    }
  }
);

export const createAudienceFromCrm = createAsyncThunk(
  'metaAds/createAudienceFromCrm',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/meta-ads/audiences/from-crm', payload);
      return response.data.data.audience;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create audience');
    }
  }
);

export const createLookalikeAudience = createAsyncThunk(
  'metaAds/createLookalikeAudience',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/meta-ads/audiences/lookalike', payload);
      return response.data.data.audience;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create lookalike audience');
    }
  }
);

export const refreshAudienceSizes = createAsyncThunk(
  'metaAds/refreshAudienceSizes',
  async (adAccountId, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/meta-ads/audiences/refresh-sizes', {}, { params: { adAccountId } });
      return response.data.data.audiences || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to refresh audience sizes');
    }
  }
);

export const deleteManagedAudience = createAsyncThunk(
  'metaAds/deleteManagedAudience',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/marketing/meta-ads/audiences/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete audience');
    }
  }
);

// ─── Insights ────────────────────────────────────────────
export const fetchReachEstimate = createAsyncThunk(
  'metaAds/fetchReachEstimate',
  async ({ adAccountId, optimizationGoal, ...targeting }, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/meta-ads/targeting/reach-estimate', { adAccountId, optimizationGoal, ...targeting });
      return response.data.data.estimate;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to estimate reach');
    }
  }
);

export const generateAdCopy = createAsyncThunk(
  'metaAds/generateAdCopy',
  async ({ productDescription, objective }, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/meta-ads/creative/generate-copy', { productDescription, objective });
      return response.data.data.variants || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to generate ad copy');
    }
  }
);

export const fetchCampaignInsights = createAsyncThunk(
  'metaAds/fetchCampaignInsights',
  async (campaignId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/marketing/meta-ads/campaigns/${campaignId}/insights`);
      return { campaignId, insights: response.data.data.insights };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch insights');
    }
  }
);

// ─── Analytics ───────────────────────────────────────────
export const fetchAnalyticsOverview = createAsyncThunk(
  'metaAds/fetchAnalyticsOverview',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/analytics/overview', { params });
      return response.data.data.overview;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch analytics overview');
    }
  }
);

export const fetchAnalyticsTimeseries = createAsyncThunk(
  'metaAds/fetchAnalyticsTimeseries',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/analytics/timeseries', { params });
      return response.data.data.series || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch analytics time series');
    }
  }
);

export const fetchAnalyticsCampaigns = createAsyncThunk(
  'metaAds/fetchAnalyticsCampaigns',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/analytics/campaigns', { params });
      return response.data.data.campaigns || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch campaign analytics');
    }
  }
);

export const fetchAnalyticsAdSets = createAsyncThunk(
  'metaAds/fetchAnalyticsAdSets',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/analytics/adsets', { params });
      return response.data.data.adSets || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ad set analytics');
    }
  }
);

export const fetchAnalyticsAds = createAsyncThunk(
  'metaAds/fetchAnalyticsAds',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/analytics/ads', { params });
      return response.data.data.ads || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ad-level analytics');
    }
  }
);

export const fetchAnalyticsHistory = createAsyncThunk(
  'metaAds/fetchAnalyticsHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/analytics/history', { params });
      return response.data.data.history || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch analytics history');
    }
  }
);

export const fetchAnalyticsSuggestions = createAsyncThunk(
  'metaAds/fetchAnalyticsSuggestions',
  async ({ adAccountId, force = false } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/meta-ads/analytics/suggestions', {
        params: { adAccountId, ...(force ? { force: 'true' } : {}) },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to generate AI suggestions');
    }
  }
);

export const syncNow = createAsyncThunk(
  'metaAds/syncNow',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/meta-ads/sync-now');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Sync failed');
    }
  }
);

// ─── Selected ad account ───────────────────────────────────
export const setSelectedAdAccount = createAsyncThunk(
  'metaAds/setSelectedAdAccount',
  async (adAccountId, { rejectWithValue }) => {
    try {
      const response = await api.put('/marketing/meta-ads/selected-account', { adAccountId });
      return response.data.data.selectedAdAccountId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to save selected ad account');
    }
  }
);

// ─── Slice ───────────────────────────────────────────────
const initialState = {
  connected: false,
  configured: false, // whether app credentials are saved
  appId: null,
  credential: null, // { appId, webhookVerifyToken, ... } — server never returns appSecret
  urls: null, // { callbackUrl, webhookUrl } — paste into the tenant's own Meta App Dashboard
  connectUrl: null, // set once fetchMetaOAuthUrl resolves; page redirects to it then clears it
  tokenStatus: null,
  adAccounts: [],
  campaigns: [],
  selectedCampaign: null,
  loading: false,
  error: null,
  creatingAd: false,
  adProgress: null, // { step, message }
  interestResults: [],
  locationResults: [],
  pages: [],
  adSetsByCampaign: {}, // { [campaignId]: AdSet[] }
  adsByCampaign: {}, // { [campaignId]: Ad[] }
  analyticsOverview: null,
  analyticsTimeseries: [],
  analyticsCampaigns: [],
  analyticsAdSets: [],
  analyticsAds: [],
  managedAudiences: [],
  managedAudiencesLoading: false,
  audienceActionLoading: false,
  analyticsBreakdown: [],
  analyticsHistory: [],
  analyticsLoading: false,
  selectedAdAccountId: null,
  suggestions: null,
  suggestionsLoading: false
};

const metaAdsSlice = createSlice({
  name: 'metaAds',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    selectCampaign: (state, action) => {
      state.selectedCampaign = action.payload;
    },
    setAdProgress: (state, action) => {
      state.adProgress = action.payload;
    },
    clearConnectUrl: (state) => {
      state.connectUrl = null;
    }
  },
  extraReducers: (builder) => {
    // Credentials
    builder.addCase(saveMetaCredentials.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(saveMetaCredentials.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.configured = true;
      state.credential = action.payload.credential || state.credential;
    });
    builder.addCase(saveMetaCredentials.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(fetchCredentialsStatus.fulfilled, (state, action) => {
      state.configured = action.payload.configured || false;
      state.appId = action.payload.appId || null;
      state.credential = action.payload.credential || null;
    });

    builder.addCase(fetchOAuthUrls.fulfilled, (state, action) => {
      state.urls = action.payload;
    });

    // OAuth
    builder.addCase(fetchMetaOAuthUrl.fulfilled, (state, action) => {
      state.loading = false;
      state.connectUrl = action.payload.url;
    });
    builder.addCase(fetchMetaOAuthUrl.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(connectWithAccessToken.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(connectWithAccessToken.fulfilled, (state) => {
      state.loading = false;
      state.connected = true;
    });
    builder.addCase(connectWithAccessToken.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(fetchConnectionStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchConnectionStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.connected = action.payload.connected || false;
      state.configured = action.payload.configured || false;
      state.appId = action.payload.appId || null;
      state.tokenStatus = {
        expiresAt: action.payload.expiresAt,
        businessId: action.payload.businessId,
        businessName: action.payload.businessName,
        adAccountIds: action.payload.adAccountIds
      };
      state.selectedAdAccountId = action.payload.selectedAdAccountId || null;
    });
    builder.addCase(fetchConnectionStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.connected = false;
    });

    builder.addCase(disconnectMeta.fulfilled, (state) => {
      state.connected = false;
      state.tokenStatus = null;
      state.adAccounts = [];
      state.campaigns = [];
    });

    // Ad Accounts
    builder.addCase(fetchAdAccounts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAdAccounts.fulfilled, (state, action) => {
      state.loading = false;
      state.adAccounts = action.payload;
    });
    builder.addCase(fetchAdAccounts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Campaigns
    builder.addCase(fetchCampaigns.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCampaigns.fulfilled, (state, action) => {
      state.loading = false;
      state.campaigns = action.payload;
    });
    builder.addCase(fetchCampaigns.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(createCampaign.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createCampaign.fulfilled, (state, action) => {
      state.loading = false;
      state.campaigns.unshift(action.payload);
    });
    builder.addCase(createCampaign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(updateCampaign.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateCampaign.fulfilled, (state, action) => {
      state.loading = false;
      const idx = state.campaigns.findIndex(c => c._id === action.payload._id);
      if (idx > -1) {
        state.campaigns[idx] = action.payload;
      }
    });
    builder.addCase(updateCampaign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(deleteCampaign.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteCampaign.fulfilled, (state, action) => {
      state.loading = false;
      state.campaigns = state.campaigns.filter(c => c._id !== action.payload);
    });
    builder.addCase(deleteCampaign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(duplicateCampaign.fulfilled, (state, action) => {
      state.campaigns.unshift(action.payload.campaign);
    });
    builder.addCase(duplicateCampaign.rejected, (state, action) => {
      state.error = action.payload;
    });

    // Insights
    builder.addCase(fetchCampaignInsights.fulfilled, (state, action) => {
      const idx = state.campaigns.findIndex(c => c._id === action.payload.campaignId);
      if (idx > -1) {
        state.campaigns[idx].insights = action.payload.insights;
      }
    });

    // Creative & Ad
    builder.addCase(uploadCreativeImage.pending, (state) => {
      state.creatingAd = true;
    });
    builder.addCase(uploadCreativeImage.fulfilled, (state) => {
      state.creatingAd = false;
    });
    builder.addCase(uploadCreativeImage.rejected, (state, action) => {
      state.creatingAd = false;
      state.error = action.payload;
    });

    builder.addCase(createAd.pending, (state) => {
      state.creatingAd = true;
    });
    builder.addCase(createAd.fulfilled, (state) => {
      state.creatingAd = false;
    });
    builder.addCase(createAd.rejected, (state, action) => {
      state.creatingAd = false;
      state.error = action.payload;
    });

    // Targeting Search
    builder.addCase(searchInterests.fulfilled, (state, action) => {
      state.interestResults = action.payload;
    });
    builder.addCase(searchLocations.fulfilled, (state, action) => {
      state.locationResults = action.payload;
    });
    builder.addCase(fetchPages.fulfilled, (state, action) => {
      state.pages = action.payload;
    });

    // Ad Sets / Ads management (publish, pause, resume)
    builder.addCase(fetchAdSets.fulfilled, (state, action) => {
      state.adSetsByCampaign[action.payload.campaignId] = action.payload.adSets;
    });
    builder.addCase(updateAdSet.fulfilled, (state, action) => {
      const list = state.adSetsByCampaign[action.payload.campaignId] || [];
      const idx = list.findIndex((a) => a._id === action.payload._id);
      if (idx > -1) list[idx] = action.payload;
    });
    builder.addCase(fetchAds.fulfilled, (state, action) => {
      state.adsByCampaign[action.payload.campaignId] = action.payload.ads;
    });
    builder.addCase(updateAd.fulfilled, (state, action) => {
      const list = state.adsByCampaign[action.payload.campaignId] || [];
      const idx = list.findIndex((a) => a._id === action.payload._id);
      if (idx > -1) list[idx] = action.payload;
    });
    builder.addCase(publishAd.fulfilled, (state, action) => {
      const { campaignId, campaign, adSet, ad } = action.payload;
      const campaignIdx = state.campaigns.findIndex((c) => c._id === campaign._id);
      if (campaignIdx > -1) state.campaigns[campaignIdx] = campaign;
      const adSets = state.adSetsByCampaign[campaignId] || [];
      const adSetIdx = adSets.findIndex((a) => a._id === adSet._id);
      if (adSetIdx > -1) adSets[adSetIdx] = adSet;
      const ads = state.adsByCampaign[campaignId] || [];
      const adIdx = ads.findIndex((a) => a._id === ad._id);
      if (adIdx > -1) ads[adIdx] = ad;
    });
    builder.addCase(publishAd.rejected, (state, action) => {
      state.error = action.payload;
    });

    // Analytics
    builder.addCase(fetchAnalyticsOverview.pending, (state) => {
      state.analyticsLoading = true;
    });
    builder.addCase(fetchAnalyticsOverview.fulfilled, (state, action) => {
      state.analyticsLoading = false;
      state.analyticsOverview = action.payload;
    });
    builder.addCase(fetchAnalyticsOverview.rejected, (state, action) => {
      state.analyticsLoading = false;
      state.error = action.payload;
    });
    builder.addCase(fetchAnalyticsTimeseries.fulfilled, (state, action) => {
      state.analyticsTimeseries = action.payload;
    });
    builder.addCase(fetchAnalyticsCampaigns.fulfilled, (state, action) => {
      state.analyticsCampaigns = action.payload;
    });
    builder.addCase(fetchAnalyticsAdSets.fulfilled, (state, action) => {
      state.analyticsAdSets = action.payload;
    });
    builder.addCase(fetchAnalyticsAds.fulfilled, (state, action) => {
      state.analyticsAds = action.payload;
    });
    builder.addCase(fetchManagedAudiences.pending, (state) => { state.managedAudiencesLoading = true; });
    builder.addCase(fetchManagedAudiences.fulfilled, (state, action) => {
      state.managedAudiencesLoading = false;
      state.managedAudiences = action.payload;
    });
    builder.addCase(fetchManagedAudiences.rejected, (state) => { state.managedAudiencesLoading = false; });
    builder.addCase(refreshAudienceSizes.fulfilled, (state, action) => {
      state.managedAudiences = action.payload;
    });
    builder.addCase(createAudienceFromCrm.pending, (state) => { state.audienceActionLoading = true; });
    builder.addCase(createAudienceFromCrm.fulfilled, (state, action) => {
      state.audienceActionLoading = false;
      state.managedAudiences = [action.payload, ...state.managedAudiences];
    });
    builder.addCase(createAudienceFromCrm.rejected, (state) => { state.audienceActionLoading = false; });
    builder.addCase(createLookalikeAudience.pending, (state) => { state.audienceActionLoading = true; });
    builder.addCase(createLookalikeAudience.fulfilled, (state, action) => {
      state.audienceActionLoading = false;
      state.managedAudiences = [action.payload, ...state.managedAudiences];
    });
    builder.addCase(createLookalikeAudience.rejected, (state) => { state.audienceActionLoading = false; });
    builder.addCase(deleteManagedAudience.fulfilled, (state, action) => {
      state.managedAudiences = state.managedAudiences.filter((a) => a._id !== action.payload);
    });
    builder.addCase(fetchAnalyticsBreakdown.fulfilled, (state, action) => {
      state.analyticsBreakdown = action.payload;
    });
    builder.addCase(fetchAnalyticsHistory.fulfilled, (state, action) => {
      state.analyticsHistory = action.payload;
    });

    builder.addCase(fetchAnalyticsSuggestions.pending, (state) => {
      state.suggestionsLoading = true;
    });
    builder.addCase(fetchAnalyticsSuggestions.fulfilled, (state, action) => {
      state.suggestionsLoading = false;
      state.suggestions = action.payload;
    });
    builder.addCase(fetchAnalyticsSuggestions.rejected, (state, action) => {
      state.suggestionsLoading = false;
      state.error = action.payload;
    });

    builder.addCase(setSelectedAdAccount.fulfilled, (state, action) => {
      state.selectedAdAccountId = action.payload;
    });
  }
});

export const { clearError, selectCampaign, setAdProgress, clearConnectUrl } = metaAdsSlice.actions;
export default metaAdsSlice.reducer;
